import React, { useState } from "react";
import { ChevronDown, ChevronUp, Copy, ImagePlus, Link2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "./ImageUploader";
import MediaPickerDialog from "./MediaPickerDialog";
import RemoveImageDialog from "./RemoveImageDialog";

/**
 * Product image list: upload, pick from library, paste URL, reorder, remove with cloud option.
 */
export default function ProductImagesEditor({ images, onChange }) {
    const [pasteUrl, setPasteUrl] = useState("");
    const [pickerOpen, setPickerOpen] = useState(false);
    const [removeTarget, setRemoveTarget] = useState(null);

    const addUrl = (url) => {
        const trimmed = (url || "").trim();
        if (!trimmed) return;
        if (images.includes(trimmed)) {
            toast.message("Image already in list");
            return;
        }
        onChange([...images, trimmed]);
    };

    const move = (idx, dir) => {
        const next = [...images];
        const j = idx + dir;
        if (j < 0 || j >= next.length) return;
        [next[idx], next[j]] = [next[j], next[idx]];
        onChange(next);
    };

    const copyUrl = (url) => {
        navigator.clipboard.writeText(url).then(() => toast.success("URL copied"));
    };

    return (
        <div className="col-span-2" data-testid="product-images-editor">
            <p className="text-[11px] uppercase tracking-[0.22em] text-ink-500 mb-1.5">Product images</p>
            <p className="text-xs text-ink-500 mb-3 leading-relaxed">
                Upload 4–5 photos (1200–1600px, under 1 MB each). First image is the main shop thumbnail. Drag order with Up/Down.
            </p>

            <ImageUploader onUploadSuccess={(url) => addUrl(url)} />

            <div className="flex flex-wrap gap-2 mt-4">
                <button
                    type="button"
                    onClick={() => setPickerOpen(true)}
                    className="btn-secondary text-xs inline-flex items-center gap-1.5"
                    data-testid="pick-from-library-btn"
                >
                    <ImagePlus size={14} /> Choose from library
                </button>
            </div>

            <div className="mt-4 flex gap-2">
                <div className="flex-1 relative">
                    <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
                    <input
                        type="url"
                        value={pasteUrl}
                        onChange={(e) => setPasteUrl(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                addUrl(pasteUrl);
                                setPasteUrl("");
                            }
                        }}
                        placeholder="Paste image URL and press Enter or Add"
                        className="w-full bg-ivory-100 rounded-xl pl-10 pr-4 py-2.5 text-sm border border-sage-300/30 focus:outline-none focus:ring-2 focus:ring-sage-300"
                        data-testid="paste-image-url"
                    />
                </div>
                <button
                    type="button"
                    onClick={() => {
                        addUrl(pasteUrl);
                        setPasteUrl("");
                    }}
                    className="btn-secondary text-sm shrink-0"
                >
                    Add
                </button>
            </div>

            {images.length > 0 && (
                <ul className="mt-4 space-y-3">
                    {images.map((url, idx) => (
                        <li
                            key={`${url}-${idx}`}
                            className="flex items-center gap-3 rounded-xl border border-sage-300/30 bg-ivory-50/60 p-2"
                        >
                            <div className="h-20 w-20 rounded-lg overflow-hidden shrink-0 bg-ivory-100">
                                <img src={url} alt="" className="h-full w-full object-cover" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs text-ink-500 truncate" title={url}>
                                    {idx === 0 ? "Primary · " : ""}
                                    {url}
                                </p>
                                <div className="flex flex-wrap items-center gap-2 mt-2">
                                    <button
                                        type="button"
                                        onClick={() => copyUrl(url)}
                                        className="text-xs text-sage-700 hover:text-sage-600 inline-flex items-center gap-1"
                                    >
                                        <Copy size={12} /> Copy URL
                                    </button>
                                    <button
                                        type="button"
                                        disabled={idx === 0}
                                        onClick={() => move(idx, -1)}
                                        className="text-xs text-ink-500 disabled:opacity-30 inline-flex items-center gap-0.5"
                                    >
                                        <ChevronUp size={14} /> Up
                                    </button>
                                    <button
                                        type="button"
                                        disabled={idx === images.length - 1}
                                        onClick={() => move(idx, 1)}
                                        className="text-xs text-ink-500 disabled:opacity-30 inline-flex items-center gap-0.5"
                                    >
                                        <ChevronDown size={14} /> Down
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setRemoveTarget({ url, idx })}
                                        className="text-xs text-blush-500 hover:text-blush-600 inline-flex items-center gap-1 ml-auto"
                                    >
                                        <Trash2 size={12} /> Remove
                                    </button>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            <MediaPickerDialog
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onSelect={(url) => addUrl(url)}
                existingUrls={images}
            />

            <RemoveImageDialog
                open={!!removeTarget}
                onOpenChange={(open) => !open && setRemoveTarget(null)}
                imageUrl={removeTarget?.url}
                onRemoved={async () => {
                    const idx = removeTarget.idx;
                    onChange(images.filter((_, j) => j !== idx));
                    setRemoveTarget(null);
                }}
            />
        </div>
    );
}
